import { Injectable } from '@angular/core';
import { ClockService, ComplaintAppointment, ComplaintSparePart, DocumentAttachment, IccDocument, IccDocumentPosition, SalesDocument, SalesDocumentPosition, Timestamp } from '@icc/b2b/shared';
import { ConfigService } from '@icc/shared';
import { core } from '@icc/shared/helpers';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map } from 'rxjs/operators';
import * as SalesDocumentsActions from './sales-documents.actions';

@Injectable()
export class SalesDocumentsOperationsEffects {

    addDoc$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.addDoc),
            map(({doc}) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'addDoc',
                            docId: doc.uuid,
                            changes: doc,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                })
            })
        )
    );
    addDocWithPosition$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.addDocWithPosition),
            map(({doc, position}) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'addDoc',
                            docId: doc.uuid,
                            changes: doc,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                        {
                            id: core.generateUUID(),
                            type: 'addPosition',
                            docId: position.documentId,
                            positionId: position.uuid,
                            changes: position,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                    selectedId: doc.uuid,
                })
            })
        )
    );
    changeDoc$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.changeDoc),
            map((doc) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: Object.keys(doc.changes)
                        .filter((key) => key !== 'id' && key !== 'type')
                        .map((key: keyof IccDocument) => ({
                            id: core.generateUUID(),
                            type: 'setField',
                            docId: doc.id,
                            field: key,
                            changes: doc.changes[key as 'id' | 'name' | 'status'],
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        })),
                    statusSystems,
                })
            })
        )
    );
    addPosition$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.addPosition),
            map(({ position }) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'addPosition',
                            docId: position.documentId,
                            positionId: position.uuid,
                            changes: position,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                })
            })
        )
    );
    addAppointment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.addAppointment),
            map(({ appointment }) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'addAppointment',
                            docId: appointment.documentId,
                            appointmentId: appointment.uuid,
                            changes: appointment,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                })
            })
        )
    );
    addComplaintNote$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.addComplaintNote),
            map(({ complaintNote }) => {
                let userId = 0;
                const statusSystems = this.configService.config?.userData?.statusSystems;
                if (complaintNote.authorCompanyId !== 0 && complaintNote.authorUserId !== 0) {
                    userId = this.configService.config?.userData?.id;
                }
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'addComplaintNote',
                            docId: complaintNote.documentId,
                            complaintNoteId: complaintNote.uuid,
                            changes: complaintNote,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                })
            })
        )
    );
    addComplaintSparePart$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.addComplaintSparePart),
            map(({ complaintSparePart }) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'addComplaintSparePart',
                            docId: complaintSparePart.documentId,
                            complaintSparePartId: complaintSparePart.uuid,
                            changes: complaintSparePart,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                })
            })
        )
    );
    changePosition$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.changePosition),
            map((doc) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: (Object.keys(doc.changes) as (keyof IccDocumentPosition)[])
                        .filter((key) => key !== 'id' && key !== 'type')
                        .map((key) => ({
                            id: core.generateUUID(),
                            type: 'setPositionField',
                            docId: doc.docId,
                            positionId: doc.id,
                            field: key,
                            changes: doc.changes[key as keyof IccDocumentPosition],
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        })),
                    statusSystems,
                })
            })
        )
    );
    splitedDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.splitedDocument),
            map((doc) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'splitedDocument',
                            docId: doc.docId,
                            changes: doc.changes as {documentsUuids: string[]},
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        }
                    ],
                    statusSystems,
                })
            })
        )
    );
    changeAppointment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.changeAppointment),
            map((doc) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: (Object.keys(doc.changes) as (keyof ComplaintAppointment)[])
                        .filter((key) => key !== 'id')
                        .map((key) => ({
                            id: core.generateUUID(),
                            type: 'setAppointmentField',
                            docId: doc.docId,
                            appointmentId: doc.id,
                            field: key,
                            changes: doc.changes[key as keyof ComplaintAppointment],
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        })),
                    statusSystems,
                })
            })
        )
    );
    changeComplaintSparePart$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.changeComplaintSparePart),
            map((doc) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: (Object.keys(doc.changes) as (keyof ComplaintSparePart)[])
                        .filter((key) => key !== 'id')
                        .map((key) => ({
                            id: core.generateUUID(),
                            type: 'setComplaintSparePartField',
                            docId: doc.docId,
                            complaintSparePartId: doc.id,
                            field: key,
                            changes: doc.changes[key as keyof ComplaintSparePart],
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        })),
                    statusSystems,
                })
            })
        )
    );
    changePositionPrice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.changePositionPrice),
            map((doc) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [{
                        id: core.generateUUID(),
                        type: 'setPositionPrices',
                        docId: doc.docId,
                        positionId: doc.id,
                        changes: doc.changes,
                        machine: ClockService.getClock().timestamp.node(),
                        timestamp: Timestamp.send(ClockService.getClock()).toString(),
                        userId,
                    }],
                    statusSystems,
                })
            })
        )
    );
    changePositionBasePrice$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.changePositionBasePrice),
            map((doc) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [{
                        id: core.generateUUID(),
                        type: 'setPositionBasePrices',
                        docId: doc.docId,
                        positionId: doc.id,
                        changes: doc.changes,
                        machine: ClockService.getClock().timestamp.node(),
                        timestamp: Timestamp.send(ClockService.getClock()).toString(),
                        userId,
                    }],
                    statusSystems,
                })
            })
        )
    );
    joinDocuments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.joinDocuments),
            map((join) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'joinDocuments',
                            docId: join.salesDocumentId,
                            buyDocId: join.buyDocumentId,
                            changes: {
                                uuid: join.uuid
                            },
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                })
            })
        )
    );
    joinAttachments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.joinAttachments),
            map((join) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'joinAttachments',
                            docId: join.salesDocumentId,
                            buyDocId: join.buyDocumentId,
                            salesAttachmentId: join.salesAttachmentId,
                            buyAttachmentId: join.buyAttachmentId,
                            changes: {
                                uuid: join.uuid
                            },
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                })
            })
        )
    );
    detachDocuments$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.detachDocuments),
            map((join) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'detachDocuments',
                            docId: join.salesDocumentId,
                            buyDocId: join.buyDocumentId,
                            changes: null,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                })
            })
        )
    );
    joinPositions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.joinPositions),
            map((join) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'joinPositions',
                            docId: join.salesDocumentId,
                            buyDocId: join.buyDocumentId,
                            salesPositionId: join.salesPositionId,
                            buyPositionId: join.buyPositionId,
                            changes: {
                                uuid: join.uuid
                            },
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                })
            })
        )
    );
    detachPositions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.detachPositions),
            map((join) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'detachPositions',
                            docId: join.salesDocumentId,
                            buyDocId: join.buyDocumentId,
                            salesPositionId: join.salesPositionId,
                            buyPositionId: join.buyPositionId,
                            changes: null,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                })
            })
        )
    );
    addAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.addAttachment),
            map(({attachment}) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'addAttachment',
                            docId: attachment.documentId,
                            attachmentId: attachment.uuid,
                            changes: attachment,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                })
            })
        )
    );
    changeAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.changeAttachment),
            map((attachment) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: (Object.keys(attachment.changes) as (keyof DocumentAttachment)[])
                        .filter((key) => key !== 'id' && key !== 'type')
                        .map((key) => ({
                            id: core.generateUUID(),
                            type: 'setAttachmentField',
                            docId: attachment.docId,
                            attachmentId: attachment.id,
                            field: key,
                            changes: attachment.changes[key as keyof DocumentAttachment],
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        })),
                    statusSystems,
                })
            })
        )
    );
    removeAttachment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.removeAttachment),
            map((doc) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'removeAttachment',
                            docId: doc.docId,
                            attachmentId: doc.id,
                            changes: null,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        }
                    ],
                    statusSystems,
                })
            })
        )
    );
    removeAppointment$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.removeAppointment),
            map((doc) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'removeAppointment',
                            docId: doc.docId,
                            appointmentId: doc.id,
                            changes: null,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        }
                    ],
                    statusSystems,
                })
            })
        )
    );
    removeComplaintSparePart$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.removeComplaintSparePart),
            map((doc) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'removeComplaintSparePart',
                            docId: doc.docId,
                            complaintSparePartId: doc.id,
                            changes: doc.complaintSparePart,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        }
                    ],
                    statusSystems,
                })
            })
        )
    );
    tagDoc$ = createEffect(() =>
        this.actions$.pipe(
            ofType(SalesDocumentsActions.tagDoc),
            map((action) => {
                const statusSystems = this.configService.config?.userData?.statusSystems;
                const userId = this.configService.config?.userData?.id;
                return SalesDocumentsActions.mergeOps({
                    operations: [
                        {
                            id: core.generateUUID(),
                            type: 'tagDoc',
                            docId: action.docId,
                            changes: action.changeTag,
                            machine: ClockService.getClock().timestamp.node(),
                            timestamp: Timestamp.send(ClockService.getClock()).toString(),
                            userId,
                        },
                    ],
                    statusSystems,
                })
            })
        )
    );


    constructor(
        private actions$: Actions,
        private configService: ConfigService    ) {}
}
